import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  CheckCircle,
  Add,
  Task as TaskIcon,
  Group,
  Grass,
  WbSunny,
  Water,
  BugReport,
  Agriculture,
  CleaningServices,
  ContentCut,
  LocalFlorist,
  DateRange,
  EmojiEvents,
  Email,
  Phone
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TaskTemplate {
  title: string;
  description: string;
  category: string;
  priority: string;
  xpReward: number;
  recurring?: {
    enabled: boolean;
    frequency: string;
  };
}

const categoryIcons: { [key: string]: any } = {
  maintenance: Agriculture,
  watering: Water,
  fertilizing: Grass,
  pruning: ContentCut,
  harvesting: LocalFlorist,
  pest_control: BugReport,
  planting: Grass,
  weeding: CleaningServices,
  preparation: Agriculture,
  observation: WbSunny,
  other: TaskIcon
};

const OnboardingComplete: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<any>(location.state?.entity);
  const [entityType] = useState(location.state?.entityType || 'garden');
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [createTaskDialog, setCreateTaskDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [customTask, setCustomTask] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    dueDate: '',
    recurring: {
      enabled: false,
      frequency: 'weekly'
    }
  });
  const [inviteData, setInviteData] = useState({
    email: '',
    phone: '',
    role: 'member',
    message: ''
  });

  useEffect(() => {
    loadTaskTemplates();
  }, [entityType]);

  const loadTaskTemplates = async () => {
    try {
      const response = await api.get('/tasks/templates', {
        params: { entityType }
      });
      setTaskTemplates(response.data);
    } catch (error) {
      console.error('Error loading task templates:', error);
    }
  };

  const handleSelectTask = (index: number) => {
    const taskId = `template-${index}`;
    if (selectedTasks.includes(taskId)) {
      setSelectedTasks(selectedTasks.filter(id => id !== taskId));
    } else {
      setSelectedTasks([...selectedTasks, taskId]);
    }
  };

  const handleCreateTasks = async () => {
    setLoading(true);
    try {
      const tasksToCreate = selectedTasks.map(taskId => {
        const index = parseInt(taskId.split('-')[1]);
        const template = taskTemplates[index];
        return {
          ...template,
          entityType,
          entityId: entity._id,
          assignedTo: [user?.id]
        };
      });

      // Add custom task if provided
      if (customTask.title && customTask.category) {
        const newTask: any = {
          title: customTask.title,
          description: customTask.description,
          category: customTask.category,
          priority: customTask.priority,
          entityType,
          entityId: entity._id,
          assignedTo: [user?.id],
          xpReward: 10
        };
        
        if (customTask.dueDate) {
          newTask.dueDate = customTask.dueDate;
        }
        
        if (customTask.recurring.enabled) {
          newTask.recurring = customTask.recurring;
        }
        
        tasksToCreate.push(newTask);
      }

      // Create all tasks
      const promises = tasksToCreate.map(task => 
        api.post('/tasks', task)
      );
      
      await Promise.all(promises);

      // Navigate to dashboard with success message
      navigate('/dashboard', {
        state: {
          message: `Created ${tasksToCreate.length} tasks for your ${entityType}!`,
          entityType,
          entityId: entity._id
        }
      });
    } catch (error) {
      console.error('Error creating tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteCollaborator = async () => {
    setLoading(true);
    try {
      const endpoint = entityType === 'garden' 
        ? `/gardens/${entity._id}/collaborators`
        : `/farms/${entity.farm || entity._id}/invite`;

      await api.post(endpoint, inviteData);
      
      setInviteDialog(false);
      setInviteData({ email: '', phone: '', role: 'member', message: '' });
      
      // Show success message
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getNextSteps = () => {
    const steps = [];
    
    if (entityType === 'garden') {
      steps.push(
        { icon: <TaskIcon />, text: 'Create tasks to manage your garden', action: 'tasks' },
        { icon: <Group />, text: 'Invite family members to collaborate', action: 'invite' },
        { icon: <LocalFlorist />, text: 'Add plants to your garden', action: 'plants' },
        { icon: <Agriculture />, text: 'Create more gardens in this area', action: 'expand' }
      );
    } else if (entityType === 'plant') {
      steps.push(
        { icon: <Water />, text: 'Set up watering schedule', action: 'tasks' },
        { icon: <DateRange />, text: 'Track growth stages', action: 'track' },
        { icon: <Grass />, text: 'Add more plants', action: 'plants' },
        { icon: <Agriculture />, text: 'Create a garden for your plants', action: 'expand' }
      );
    }
    
    return steps;
  };

  if (!entity) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>No entity data found. Please complete onboarding first.</Typography>
        <Button onClick={() => navigate('/onboarding')} sx={{ mt: 2 }}>
          Back to Onboarding
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Congratulations! 🎉
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          You've created your first {entityType}: <strong>{entity.name}</strong>
        </Typography>
        <Chip 
          icon={<EmojiEvents />} 
          label={`+${location.state?.xpAwarded || 50} XP`} 
          color="primary" 
          sx={{ mt: 1 }}
        />
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What's Next?
          </Typography>
          <List>
            {getNextSteps().map((step, index) => (
              <ListItem key={index}>
                <ListItemIcon>{step.icon}</ListItemIcon>
                <ListItemText primary={step.text} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Suggested Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select tasks to get started with your {entityType}:
          </Typography>
          
          <Grid container spacing={2}>
            {taskTemplates.map((template, index) => {
              const Icon = categoryIcons[template.category] || TaskIcon;
              const isSelected = selectedTasks.includes(`template-${index}`);
              
              return (
                <Grid item xs={12} key={index}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => handleSelectTask(index)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <Icon sx={{ mr: 2, color: 'primary.main' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">{template.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {template.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            size="small" 
                            label={template.priority} 
                            color={template.priority === 'high' ? 'error' : 'default'}
                          />
                          <Chip 
                            size="small" 
                            label={`+${template.xpReward} XP`} 
                            color="primary"
                          />
                          {template.recurring && (
                            <Chip 
                              size="small" 
                              label={template.recurring.frequency} 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      <CheckCircle 
                        sx={{ 
                          color: isSelected ? 'primary.main' : 'action.disabled' 
                        }} 
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setCreateTaskDialog(true)}
                sx={{ py: 2 }}
              >
                Create Custom Task
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCreateTasks}
              disabled={selectedTasks.length === 0 && !customTask.title}
              startIcon={loading ? <CircularProgress size={20} /> : <TaskIcon />}
            >
              Create {selectedTasks.length} Tasks
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
            >
              Skip
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invite Collaborators
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Growing is better together! Invite family members to help manage your {entityType}.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Group />}
            onClick={() => setInviteDialog(true)}
            fullWidth
          >
            Invite Family Members
          </Button>
        </CardContent>
      </Card>

      {/* Create Custom Task Dialog */}
      <Dialog open={createTaskDialog} onClose={() => setCreateTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Custom Task</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              value={customTask.category}
              onChange={(_, newValue) => {
                if (newValue) {
                  const categoryName = newValue.charAt(0).toUpperCase() + newValue.slice(1).replace('_', ' ');
                  const defaultTitle = `${categoryName} ${entity.name}`;
                  setCustomTask({ 
                    ...customTask, 
                    category: newValue,
                    title: customTask.title || defaultTitle
                  });
                }
              }}
              options={Object.keys(categoryIcons)}
              getOptionLabel={(option) => option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
              renderOption={(props, option) => {
                const Icon = categoryIcons[option];
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ fontSize: 20 }} />
                    {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  required
                  placeholder="Search or select a category..."
                />
              )}
              fullWidth
            />
            
            <TextField
              label="Task Title"
              value={customTask.title}
              onChange={(e) => setCustomTask({ ...customTask, title: e.target.value })}
              fullWidth
              required
              helperText="Auto-generated based on category, but you can customize it"
            />
            
            <TextField
              label="Description"
              value={customTask.description}
              onChange={(e) => setCustomTask({ ...customTask, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Add any specific details or instructions..."
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={customTask.priority}
                  onChange={(e) => setCustomTask({ ...customTask, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Due Date"
                type="date"
                value={customTask.dueDate}
                onChange={(e) => setCustomTask({ ...customTask, dueDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <Divider />
            
            <Box>
              <FormControl fullWidth>
                <InputLabel>Recurrence</InputLabel>
                <Select
                  value={customTask.recurring.enabled ? customTask.recurring.frequency : 'once'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'once') {
                      setCustomTask({ 
                        ...customTask, 
                        recurring: { enabled: false, frequency: 'weekly' }
                      });
                    } else {
                      setCustomTask({ 
                        ...customTask, 
                        recurring: { enabled: true, frequency: value }
                      });
                    }
                  }}
                  label="Recurrence"
                >
                  <MenuItem value="once">Once (No recurrence)</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="biweekly">Every 2 Weeks</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="seasonal">Seasonal</MenuItem>
                </Select>
              </FormControl>
              {customTask.recurring.enabled && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Task will automatically recreate after completion
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateTaskDialog(false);
            setCustomTask({
              title: '',
              description: '',
              category: '',
              priority: 'medium',
              dueDate: '',
              recurring: { enabled: false, frequency: 'weekly' }
            });
          }}>Cancel</Button>
          <Button 
            onClick={() => {
              setCreateTaskDialog(false);
              if (customTask.title && customTask.category) {
                setSelectedTasks([...selectedTasks, 'custom']);
              }
            }} 
            variant="contained"
            disabled={!customTask.title || !customTask.category}
            startIcon={<TaskIcon />}
          >
            Add Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Collaborator Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Collaborator</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
            <TextField
              label="Phone (optional)"
              value={inviteData.phone}
              onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteData.role}
                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="member">Member (can view and update)</MenuItem>
                <MenuItem value="manager">Manager (can add/remove plants)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Personal Message (optional)"
              value={inviteData.message}
              onChange={(e) => setInviteData({ ...inviteData, message: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Join me in growing our garden together!"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleInviteCollaborator} 
            variant="contained"
            disabled={!inviteData.email || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Email />}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingComplete;